// Countdown nach dem Auflegen im Power Dialer (wie Close: nach dem Gespräch läuft sichtbar eine Zeit, dann wird
// automatisch der nächste Lead gewählt). Standard 10 s, einstellbar (Einstellungen > Telefonie, 0 = aus).
// Pause, sobald am Lead gearbeitet wird: Tippen, Klicken, ein offenes Formular oder Dialog (Notiz, Custom Activity,
// E-Mail, Follow-up). Ist 5 Sekunden Ruhe und kein Formular offen, läuft der Countdown dort weiter, wo er stand.
// Die reine Rechnung (`schritt`) steht ohne React hier, damit sie mit node --test prüfbar ist.
import { useEffect, useRef, useState } from "react";
import { STANDARD_COUNTDOWN, useOberflaeche } from "../daten/oberflaeche";
import { RUHE_MS, schritt, type CountdownStand } from "./countdownRechnung";
import { useTelefonie } from "./TelefonieProvider";

export { RUHE_MS } from "./countdownRechnung";
export type { CountdownStand } from "./countdownRechnung";

const FORMULAR = '.ls-eingabe, .km-karte, [role="dialog"], [data-formular-offen]';

/** Ist ein Formular oder Dialog offen (außerhalb der Telefon-Leiste)? */
export function formularOffen(): boolean {
  const treffer = document.querySelectorAll<HTMLElement>(FORMULAR);
  for (const el of treffer) {
    if (el.closest("[data-tel-leiste]") || el.closest("[data-countdown-ignorieren]")) continue;
    if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") continue;
    return true;
  }
  return false;
}

/** Läuft der Countdown? Nur nach einem beendeten Dialer-Anruf, solange nichts pausiert, kein Ende der Liste und keine Rückfrage. */
export function useDialerCountdown(): CountdownStand | null {
  const t = useTelefonie();
  const { countdown, geladen } = useOberflaeche();
  const d = t.dialer;
  const n = t.nacharbeit;
  const gesamt = (geladen ? countdown : STANDARD_COUNTDOWN) * 1000;
  const callId = n?.call.id ?? null;
  const aktiv =
    !!d && !d.pausiert && !t.aktiverAnruf && !!n && n.dialer && d.aktuell?.grund !== "ende" && !t.fehler && gesamt > 0 && !d.meldung;
  const [stand, setStand] = useState<CountdownStand | null>(null);
  const letzteEingabe = useRef(Date.now());
  const ausgeloest = useRef<string | null>(null);
  const weiterRef = useRef(t.dialerWeiter);
  weiterRef.current = t.dialerWeiter;

  // Eingaben am Lead merken (Tasten, Klicks, Tippen); die Leiste selbst zählt nicht
  useEffect(() => {
    const merke = (e: Event) => {
      const ziel = e.target as HTMLElement | null;
      if (ziel?.closest?.("[data-tel-leiste]")) return;
      letzteEingabe.current = Date.now();
    };
    const ereignisse = ["keydown", "pointerdown", "input", "paste"] as const;
    ereignisse.forEach((x) => document.addEventListener(x, merke, true));
    return () => ereignisse.forEach((x) => document.removeEventListener(x, merke, true));
  }, []);

  useEffect(() => {
    if (!aktiv || !callId) {
      setStand(null);
      return;
    }
    // Neuer beendeter Anruf: von vorn zählen. Die letzte Eingabe vor dem Auflegen zählt nicht als Arbeit am nächsten Lead.
    letzteEingabe.current = Math.min(letzteEingabe.current, Date.now() - RUHE_MS);
    let rest = gesamt;
    let zuletzt = performance.now();
    setStand({ rest, gesamt, haelt: false });
    const takt = window.setInterval(() => {
      const jetzt = performance.now();
      const r = schritt(rest, jetzt - zuletzt, Date.now() - letzteEingabe.current, formularOffen());
      zuletzt = jetzt;
      rest = r.rest;
      setStand((alt) => (alt && alt.rest === rest && alt.haelt === r.haelt ? alt : { rest, gesamt, haelt: r.haelt }));
      if (rest <= 0 && ausgeloest.current !== callId) {
        ausgeloest.current = callId;
        window.clearInterval(takt);
        void weiterRef.current();
      }
    }, 200);
    return () => window.clearInterval(takt);
  }, [aktiv, callId, gesamt]);

  return aktiv ? stand : null;
}
